import React from "react";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

/**
 * DataGrid component - table with primary header, sortable columns.
 * Styling: rounded-3xl, no border, shadow-sm.
 * @see .cursor/ui.md
 */
const DataGrid = ({
  columns = [],
  data = [],
  sortBy,
  sortOrder = "asc",
  onSortChange,
  emptyMessage = "No data to display",
  emptyIcon: EmptyIcon,
  emptyIllustration: EmptyIllustration,
  className = "",
  headerVariant = "primary", // primary | subtle | white
  onRowClick,
  minHeight,
}) => {
  const SortableHeader = ({
    column,
    children,
    sortable = true,
    headerClassName = "",
  }) => {
    const isActive = sortBy === column;

    return (
      <th
        className={`px-6 py-4 text-left  text-xs font-bold uppercase tracking-wider ${headerClassName} ${
          headerVariant === "primary"
            ? "bg-primary text-white"
            : headerVariant === "white"
              ? "bg-gray-50 text-gray-500 border-b border-gray-200"
              : "bg-gray-50 text-gray-600"
        } ${sortable ? "cursor-pointer hover:opacity-90" : ""} transition-opacity duration-200`}
        onClick={
          sortable && onSortChange ? () => onSortChange(column) : undefined
        }
      >
        <div className="flex items-center gap-1">
          <span>{children}</span>
          {sortable && isActive && (
            <span className="inline-flex">
              {sortOrder === "asc" ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )}
            </span>
          )}
        </div>
      </th>
    );
  };

  const isBorderless = headerVariant === "white";

  return (
    <div
      className={`overflow-hidden bg-white border-b border-gray-200 ${className}`}
    >
      <div
        className="overflow-x-auto"
        style={
          minHeight
            ? {
                minHeight:
                  typeof minHeight === "number" ? `${minHeight}px` : minHeight,
              }
            : undefined
        }
      >
        <table className="min-w-full">
          <thead>
            <tr>
              {columns.map((col) => (
                <SortableHeader
                  key={col.key}
                  column={col.key}
                  sortable={col.sortable !== false && !!onSortChange}
                  headerClassName={col.headerClassName || ""}
                >
                  {col.label}
                </SortableHeader>
              ))}
            </tr>
          </thead>
          <tbody
            className={`bg-white ${!isBorderless ? "divide-y divide-gray-200" : ""}`}
          >
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-6 py-12 text-center text-sm text-gray-500"
                >
                  <div className="flex flex-col items-center justify-center">
                    {EmptyIllustration ? (
                      <EmptyIllustration className="mb-4" />
                    ) : EmptyIcon ? (
                      <EmptyIcon className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                    ) : null}
                    <p className="text-gray-500">{emptyMessage}</p>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr
                  key={row.id ?? rowIndex}
                  className={`text-gray-700 hover:bg-gray-50 border-b border-gray-200 transition-colors duration-150 ${
                    onRowClick ? "cursor-pointer" : ""
                  }`}
                  onClick={
                    onRowClick
                      ? (e) => {
                          const target = e.target.closest("button, a");
                          if (!target) onRowClick(row);
                        }
                      : undefined
                  }
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-6 text-sm ${col.cellClassName || ""} ${
                        isBorderless ? "py-5" : "py-4"
                      }`}
                    >
                      {col.render
                        ? col.render(row[col.key], row)
                        : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataGrid;
